import { Crosshair, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { clarifyOptionIdsFor, clarifyOptionKey } from "./learning-chat-flow.js";
import type { ChatSituationId } from "./learning-chat-situations.js";

export function LearningChatThinkingPanel() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full min-h-56 flex-col items-center justify-center px-6 py-6 text-center">
      <div aria-hidden="true" className="relative mb-5 flex h-32 w-48 items-end justify-center">
        <div className="absolute top-2 left-5 h-16 w-11 -rotate-12 rounded-xl border border-border bg-background shadow-sm">
          <div className="h-1.5 rounded-t-xl bg-warning-foreground/40" />
        </div>
        <div className="absolute top-0 left-12 h-20 w-12 -rotate-6 rounded-xl border border-border bg-background shadow-sm">
          <div className="h-1.5 rounded-t-xl bg-accent/70" />
        </div>
        <div className="relative z-10 h-24 w-16 rounded-xl border border-border bg-background shadow-md">
          <div className="h-1.5 rounded-t-xl bg-info-foreground/70" />
          <div className="grid place-items-center pt-5">
            <Crosshair className="size-6 text-info-foreground" strokeWidth={1.6} />
          </div>
        </div>
        <div className="absolute top-0 right-12 h-20 w-12 rotate-6 rounded-xl border border-border bg-background shadow-sm">
          <div className="h-1.5 rounded-t-xl bg-muted-foreground/40" />
        </div>
        <div className="absolute top-2 right-5 h-16 w-11 rotate-12 rounded-xl border border-border bg-background shadow-sm">
          <div className="h-1.5 rounded-t-xl bg-success-foreground/50" />
        </div>
        <div className="absolute bottom-0 z-20 grid size-12 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg">
          <Sparkles className="size-5" strokeWidth={1.7} />
        </div>
      </div>
      <p className="text-lg font-medium text-foreground">{t("learningRoute.chatThinkingTitle")}</p>
      <p className="mt-1.5 text-sm text-muted-foreground">{t("learningRoute.chatThinkingSubtitle")}</p>
    </div>
  );
}

export function LearningChatClarifyOptions({
  step,
  situationId,
  onSelect,
  onUnknown,
}: {
  step: 0 | 1;
  situationId?: ChatSituationId;
  onSelect: (optionId: string) => void;
  onUnknown: () => void;
}) {
  const { t } = useTranslation();
  const optionIds = clarifyOptionIdsFor(situationId, step);

  return (
    <div className="space-y-2">
      {optionIds.map((id, index) => (
        <button
          className="flex w-full items-start gap-2.5 rounded-2xl border border-border bg-background px-3 py-2.5 text-left text-sm leading-snug transition-colors hover:bg-muted/25 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
          data-control-id={
            step === 0 ? `learning-chat.clarify.option.${id}` : `learning-chat.clarify.goal.${id}`
          }
          key={id}
          onClick={() => onSelect(id)}
          type="button"
        >
          <span
            aria-hidden="true"
            className="grid size-7 shrink-0 place-items-center rounded-full bg-muted text-xs font-medium text-muted-foreground"
          >
            {index + 1}
          </span>
          <span>{t(clarifyOptionKey(situationId, step, id))}</span>
        </button>
      ))}
      <button
        className="rounded-xl border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/25 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
        data-control-id="learning-chat.clarify.unknown"
        onClick={onUnknown}
        type="button"
      >
        {t("learningRoute.chatClarify.unknown")}
      </button>
    </div>
  );
}
